import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// home-page-setting-client-mod -- HomePageSettingPanel
//
// 首页路径设置面板，嵌入到 Admin 后台。
// ============================================================================
import * as React from 'react';
/** 获取 auth token（从 localStorage） */
function getAuthToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem('accessToken');
}
/** 构建包含认证信息的请求头 */
function buildAuthHeaders(extra) {
    const headers = { ...extra };
    const token = getAuthToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}
/**
 * 首页路径设置面板
 */
export function HomePageSettingPanel({ fetchService }) {
    const [path, setPath] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    // 加载当前配置
    React.useEffect(() => {
        const loadConfig = async () => {
            try {
                const res = await fetchService.fetch('/api/admin/site-settings/homepage', {
                    headers: buildAuthHeaders(),
                });
                const data = await res.json();
                setPath(data.path ?? '');
            }
            catch (err) {
                setMessage({ type: 'error', text: `加载配置失败: ${err instanceof Error ? err.message : '未知错误'}` });
            }
            finally {
                setLoading(false);
            }
        };
        loadConfig();
    }, [fetchService]);
    // 保存配置
    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetchService.fetch('/api/admin/site-settings/homepage', {
                method: 'PUT',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ path }),
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: `首页路径已更新为 ${data.path}` });
            }
            else {
                setMessage({ type: 'error', text: '保存失败' });
            }
        }
        catch (err) {
            setMessage({ type: 'error', text: `保存失败: ${err instanceof Error ? err.message : '未知错误'}` });
        }
        finally {
            setSaving(false);
        }
    };
    // 清除配置
    const handleClear = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetchService.fetch('/api/admin/site-settings/homepage', {
                method: 'DELETE',
                headers: buildAuthHeaders(),
            });
            const data = await res.json();
            if (data.success) {
                setPath('');
                setMessage({ type: 'success', text: '已清除首页路径设置，访问 / 将显示 404 页面' });
            }
            else {
                setMessage({ type: 'error', text: '清除失败' });
            }
        }
        catch (err) {
            setMessage({ type: 'error', text: `清除失败: ${err instanceof Error ? err.message : '未知错误'}` });
        }
        finally {
            setSaving(false);
        }
    };
    if (loading) {
        return (_jsx("div", { style: styles.panel, children: _jsx("div", { style: styles.loading, children: "\u52A0\u8F7D\u4E2D..." }) }));
    }
    return (_jsxs("div", { style: styles.panel, children: [_jsxs("div", { style: styles.header, children: [_jsx("h1", { style: styles.title, children: "\uD83C\uDFE0 \u9996\u9875\u8BBE\u7F6E" }), _jsx("p", { style: styles.subtitle, children: "\u914D\u7F6E\u8BBF\u95EE\u7AD9\u70B9\u6839\u8DEF\u5F84 / \u65F6\u663E\u793A\u7684\u9875\u9762\u5185\u5BB9" })] }), _jsxs("div", { style: styles.section, children: [_jsx("label", { style: styles.label, children: "\u9996\u9875\u8DEF\u5F84" }), _jsx("input", { type: "text", value: path, onChange: (e) => setPath(e.target.value), placeholder: "\u4F8B\u5982: /pwa-links", style: styles.input }), _jsx("p", { style: styles.hint, children: "\u8BBE\u7F6E\u540E\uFF0C\u8BBF\u95EE\u7AD9\u70B9\u6839\u8DEF\u5F84 / \u5C06\u81EA\u52A8\u6E32\u67D3\u8BE5\u8DEF\u5F84\u5BF9\u5E94\u7684\u9875\u9762\u5185\u5BB9\u3002 \u6D4F\u89C8\u5668\u5730\u5740\u680F\u4ECD\u663E\u793A /\u3002" })] }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { onClick: handleClear, disabled: saving || !path, style: { ...styles.button, ...styles.clearButton, opacity: (saving || !path) ? 0.5 : 1 }, children: "\u6E05\u9664\u8BBE\u7F6E" }), _jsx("button", { onClick: handleSave, disabled: saving || !path, style: { ...styles.button, ...styles.saveButton, opacity: (saving || !path) ? 0.5 : 1 }, children: saving ? '保存中...' : '保存' })] }), message && (_jsx("div", { style: {
                    ...styles.message,
                    backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
                    color: message.type === 'success' ? '#065f46' : '#991b1b',
                    borderColor: message.type === 'success' ? '#a7f3d0' : '#fecaca',
                }, children: message.text })), _jsxs("div", { style: styles.infoBox, children: [_jsx("p", { style: styles.infoTitle, children: "\uD83D\uDCA1 \u8BF4\u660E" }), _jsxs("ul", { style: styles.infoList, children: [_jsx("li", { children: "\u9996\u9875\u8DEF\u5F84\u5FC5\u987B\u4EE5 / \u5F00\u5934" }), _jsx("li", { children: "\u8DEF\u5F84\u5BF9\u5E94\u7684\u8DEF\u7531\u5FC5\u987B\u5DF2\u6CE8\u518C\uFF08\u5982 /pwa-links \u9700\u8981 PWA Links \u6A21\u5757\u5DF2\u52A0\u8F7D\uFF09" }), _jsx("li", { children: "\u5982\u679C\u76EE\u6807\u8DEF\u7531\u4E0D\u5B58\u5728\uFF0C\u8BBF\u95EE / \u5C06\u8FD4\u56DE 404" }), _jsx("li", { children: "\u6E05\u9664\u8BBE\u7F6E\u540E\uFF0C\u8BBF\u95EE / \u5C06\u6062\u590D\u4E3A\u9ED8\u8BA4 404 \u884C\u4E3A" })] })] })] }));
}
const styles = {
    panel: {
        maxWidth: '700px',
        margin: '0 auto',
        padding: '32px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    loading: {
        padding: '40px',
        textAlign: 'center',
        color: '#64748b',
    },
    header: {
        marginBottom: '28px',
    },
    title: {
        margin: '0 0 8px 0',
        fontSize: '24px',
        fontWeight: 700,
        color: '#1e293b',
    },
    subtitle: {
        margin: 0,
        fontSize: '14px',
        color: '#64748b',
    },
    section: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 600,
        color: '#374151',
        marginBottom: '6px',
    },
    input: {
        width: '100%',
        padding: '10px 14px',
        fontSize: '14px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s ease',
    },
    hint: {
        margin: '8px 0 0 0',
        fontSize: '13px',
        color: '#6b7280',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
    },
    button: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    clearButton: {
        backgroundColor: '#f3f4f6',
        color: '#4b5563',
    },
    saveButton: {
        backgroundColor: '#6366f1',
        color: '#ffffff',
    },
    message: {
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid',
        fontSize: '14px',
        marginBottom: '20px',
    },
    infoBox: {
        padding: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
    },
    infoTitle: {
        margin: '0 0 8px 0',
        fontSize: '14px',
        fontWeight: 600,
        color: '#334155',
    },
    infoList: {
        margin: 0,
        paddingLeft: '20px',
        fontSize: '13px',
        color: '#64748b',
        lineHeight: '1.8',
    },
};
//# sourceMappingURL=home-page-setting-panel.js.map